import { useColorModeValue, useToken } from '@chakra-ui/react';
import { TreemapData } from '.';

export type TreemapRectangleProps<A extends Record<string, number>> = {
  colorMap: Record<string, string>;
  dataKey: keyof A;
  valueFormatter: (value: number) => string;
  totalValue: number;
};

type AllTreeMapRectangleProps<A extends Record<string, number>> =
  TreemapRectangleProps<A> &
    TreemapData<A>[number] & {
      x: number;
      y: number;
      width: number;
      height: number;
      index: number;
    };

const getPercentage = (value: number, totalValue: number): string => {
  const pct = ((value / totalValue) * 100).toFixed(0);
  return `${pct}%`;
};

export function TreemapRectangle<A extends Record<string, number>>(
  props: TreemapRectangleProps<A>,
) {
  const allProps = props as AllTreeMapRectangleProps<A>;
  // Rect separator must match the surrounding panel so gaps look like gaps
  const [panelDark] = useToken('colors', ['figmaDark.panel']);
  const separatorColor = useColorModeValue('#fff', panelDark);
  {
    const {
      dataKey,
      x,
      y,
      width,
      height,
      colorMap,
      valueFormatter,
      name,
      totalValue,
    } = allProps;

    if (!name) {
      return null;
    }

    const value: number = allProps[dataKey];
    const inset = 2; // keep 2px inset to account for stroke and padding
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    // Calculate responsive font size based on rectangle size
    const minDimension = Math.min(width, height);
    const fontSize = Math.max(2, Math.min(16, minDimension / 8)); // Scale between 8px and 16px

    return (
      <g>
        <rect
          x={x + inset}
          y={y + inset}
          width={width - inset * 2}
          height={height - inset * 2}
          rx={4}
          ry={4}
          style={{
            fill: colorMap[name],
            stroke: separatorColor,
            strokeWidth: 2,
            strokeOpacity: 1,
          }}
        />
        <text
          x={centerX}
          y={centerY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#fff"
          fontSize={fontSize}>
          <tspan x={centerX} dy={-fontSize * 1.3}>
            {name}
          </tspan>
          <tspan x={centerX} dy={fontSize * 1.3}>
            {valueFormatter(value)}
          </tspan>
          <tspan x={centerX} dy={fontSize * 1.3}>
            {getPercentage(value, totalValue)}
          </tspan>
        </text>
      </g>
    );
  }
}
